function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2
      className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-4"
      style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
    >
      {children}
    </h2>
  );
}

function SectionText({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-[#6B5E53] text-base leading-[1.9] mb-0"
      style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
    >
      {children}
    </p>
  );
}

function EditorialImage({ src, caption }: { src: string; caption: string }) {
  return (
    <div className="my-14 md:my-20 -mx-4 md:-mx-8">
      <div className="w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-2xl bg-[#EDE6DA]">
        <img
          src={src}
          alt={caption}
          className="w-full h-full object-cover object-top"
        />
      </div>
      <p
        className="text-center text-[#B5A898] text-[11px] tracking-[0.15em] mt-4 uppercase"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        {caption}
      </p>
    </div>
  );
}

export default function RCArticleContentSecondHalf() {
  return (
    <div className="max-w-2xl mx-auto px-4 md:px-0">
      {/* Image 4 */}
      <EditorialImage
        src="https://public.readdy.ai/ai/img_res/c88a5eeac3959db71805bb4f23f6bd8c.jpg"
        caption="Quiet Corner Styling for Small Spaces"
      />

      {/* Section 7 */}
      <div className="mb-14">
        <SectionHeading>Use natural materials for a grounded look</SectionHeading>
        <SectionText>
          Natural materials bring a quality to a reading corner that manufactured alternatives simply cannot. Light oak or ash in a side table or lamp base carries warmth that changes with the daylight — lighter in the morning, richer and more amber in the afternoon. Linen and cotton fabrics age beautifully, softening with use. Woven rattan, jute, and cane add an organic texture that feels warm underfoot or in hand. These materials ground the corner in the natural world, giving it a timeless quality that trends cannot touch.
        </SectionText>
      </div>

      {/* Section 8 */}
      <div className="mb-14">
        <SectionHeading>Layer warm neutrals together</SectionHeading>
        <SectionText>
          Neutral does not mean flat. Beige, cream, ivory, sand, oatmeal, and warm white each sit at a slightly different temperature — some lean towards yellow, others towards pink, others towards grey. Layering these tones thoughtfully creates a corner that feels rich and dimensional even without a single accent colour. A cream chair, an oatmeal throw, ivory cushions, a warm white wall, and a natural oak table — together they form a palette that is complex, restful, and completely cohesive. The variations are subtle but they are what make the space feel alive.
        </SectionText>
      </div>

      {/* Section 9 */}
      <div className="mb-14">
        <SectionHeading>Place the corner where the light feels gentle</SectionHeading>
        <SectionText>
          The position of a reading corner within a room matters as much as its contents. A corner placed near a window benefits from the changing quality of natural light throughout the day — morning softness, afternoon warmth, evening calm. Sheer linen curtains that diffuse rather than block the light make this even more beautiful. If a window position is not available, a quiet alcove, a wall with no through-traffic, or a spot where the room narrows naturally can all create the sense of enclosure that makes a reading corner feel like its own small world.
        </SectionText>
      </div>

      {/* Image 5 */}
      <EditorialImage
        src="https://readdy.ai/api/search-image?query=window%20nook%20warm%20beige%20tones%20sheer%20linen%20curtains%20morning%20sunlight%20soft%20cream%20armchair%20reading%20corner%20natural%20light%20peaceful%20cozy%20intimate%20editorial%20photography%20beautiful%20calm%20atmosphere%20neutral%20palette&width=1400&height=600&seq=rc_img5&orientation=landscape"
        caption="Window Nook in Warm Beige Tones"
      />
    </div>
  );
}
